<template>
  <div class="battleplan-form">
    <StField label="Rules Text">
      <StTextarea
        v-model="localRulesText"
        :rows="4"
        placeholder="Full battleplan rules text..."
        @update:model-value="emitChange"
      />
    </StField>

    <section class="form-section">
      <div class="section-heading-row">
        <div class="st-eyebrow section-heading">Sections</div>
        <div class="section-actions">
          <StButton
            size="sm"
            variant="secondary"
            icon="plus"
            @click="addCommonSection('The Armies')"
          >
            The Armies
          </StButton>
          <StButton
            size="sm"
            variant="secondary"
            icon="plus"
            @click="addCommonSection('Objectives')"
          >
            Objectives
          </StButton>
          <StButton
            size="sm"
            variant="secondary"
            icon="plus"
            @click="addCommonSection('Deployment')"
          >
            Deployment
          </StButton>
          <StButton
            size="sm"
            variant="secondary"
            icon="plus"
            @click="addCommonSection('Glorious Victory')"
          >
            Glorious Victory
          </StButton>
        </div>
      </div>

      <EditableList
        :items="localSections"
        add-label="Add section"
        remove-label="Remove section"
        @add="addSection"
        @remove="removeSection"
      >
        <template #row="{ item: section, index }">
          <div class="section-fields">
            <StField label="Header">
              <StTextInput
                :model-value="section.title"
                placeholder="e.g. The Armies"
                @update:model-value="updateSection(index, { title: $event })"
              />
            </StField>
            <StField label="Text">
              <StTextarea
                :model-value="section.text ?? ''"
                :rows="4"
                placeholder="Rules text for this section..."
                @update:model-value="updateSection(index, { text: $event })"
              />
            </StField>
            <StField label="Abilities">
              <AbilityForm
                :model-value="section.abilities ?? []"
                @update:model-value="updateSection(index, { abilities: $event })"
              />
            </StField>
            <StButton
              v-if="!isTrailingTextOpen(index, section)"
              class="trailing-text-add"
              size="sm"
              variant="secondary"
              icon="plus"
              @click="showTrailingText(index)"
            >
              Trailing Text
            </StButton>
            <StField v-else label="Trailing Text">
              <StTextarea
                :model-value="section.trailingText ?? ''"
                :rows="4"
                placeholder="Follow-up text after ability cards..."
                @update:model-value="updateSection(index, { trailingText: $event })"
              />
            </StField>
          </div>
        </template>
      </EditableList>
    </section>

    <StField label="Twist">
      <StTextarea
        v-model="localTwist"
        :rows="4"
        placeholder="Describe the twist..."
        @update:model-value="emitChange"
      />
    </StField>

    <section class="form-section">
      <div class="st-eyebrow section-heading">Abilities</div>
      <AbilityForm v-model="localAbilities" @update:model-value="onAbilitiesChange" />
    </section>

    <section class="form-section">
      <div class="section-heading-row">
        <div class="st-eyebrow section-heading">Scoring Text</div>
        <StButton size="sm" variant="secondary" icon="align-left" @click="prefillScoringText">
          1-2-more
        </StButton>
      </div>
      <StTextarea
        v-model="localScoringText"
        :rows="4"
        placeholder="Scoring rules description..."
        @update:model-value="emitChange"
      />
    </section>

    <section class="form-section">
      <div class="section-heading-row">
        <div class="st-eyebrow section-heading">Scoring Rules</div>
        <StButton
          size="sm"
          variant="secondary"
          icon="wand-sparkles"
          :disabled="!localScoringText.trim()"
          @click="autoPopulateScoring"
        >
          Generate from scoring text
        </StButton>
      </div>
      <ScoringRuleForm v-model="localScoringRules" @update:model-value="onScoringRulesChange" />
    </section>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import {
  parseScoringRules,
  type IAbility,
  type IScoringRule,
  type IBattleplanContentSection,
} from '@scrolldex/shared';
import { useWorkspace } from '../state/workspace.js';
import ScoringRuleForm from './ScoringRuleForm.vue';
import AbilityForm from './AbilityForm.vue';
import EditableList from './EditableList.vue';
import { StField, StTextarea, StButton, StTextInput } from './ui/index.js';

const props = defineProps<{
  currentPlan: string | null;
}>();

const ws = useWorkspace();

// The common GHB primary scoring block ("1-2-more"). Uses "* " bullets so it both
// renders as bullets and parses into structured rules via Generate.
const DEFAULT_SCORING_TEXT = [
  'Each player scores victory points at the end of each of their turns as follows:',
  '* Score 3 victory points if you control at least 1 objective.',
  '* Score 3 victory points if you control 2 or more objectives.',
  '* Score 4 victory points if you control more objectives than your opponent.',
].join('\n');

// Local reactive fields. The plan name is edited in the header (TitleBar), not here.
const localTwist = ref('');
const localRulesText = ref('');
const localScoringText = ref('');
const localScoringRules = ref<IScoringRule[]>([]);
const localAbilities = ref<IAbility[]>([]);
const localSections = ref<IBattleplanContentSection[]>([]);
const trailingTextOpen = ref<Set<number>>(new Set());

function loadFromStore(planName: string | null) {
  if (!planName) return;
  const existing = ws.authored.value[planName];
  localTwist.value = stripTwistPrefix(existing?.twist);
  localRulesText.value = existing?.rulesText ?? '';
  localScoringText.value = existing?.scoringText ?? '';
  localScoringRules.value = [...(existing?.scoringRules ?? [])];
  localAbilities.value = [...(existing?.abilities ?? [])];
  localSections.value = (existing?.sections ?? []).map(cloneSection);
  trailingTextOpen.value = new Set();
}

// Load when plan changes
watch(() => props.currentPlan, loadFromStore, { immediate: true });

function emitChange() {
  const plan = props.currentPlan;
  if (!plan) return;
  ws.setPlanFields(plan, {
    rulesText: localRulesText.value,
    twist: localTwist.value || undefined,
    scoringText: localScoringText.value || undefined,
    scoringRules: localScoringRules.value,
    abilities: localAbilities.value,
    sections: localSections.value.map(cleanSection).filter(hasSectionContent),
  });
}

function onScoringRulesChange(rules: IScoringRule[]) {
  localScoringRules.value = rules;
  emitChange();
}

// Prefill the scoring text with the default "1-2-more" block.
function prefillScoringText() {
  localScoringText.value = DEFAULT_SCORING_TEXT;
  emitChange();
}

// Clear and regenerate the structured scoring rules from the scoring text, using
// the same parser the app uses to build them (parseScoringRules in shared).
function autoPopulateScoring() {
  localScoringRules.value = parseScoringRules(
    localScoringText.value,
    props.currentPlan ?? undefined,
  );
  emitChange();
}

function onAbilitiesChange(abilities: IAbility[]) {
  localAbilities.value = abilities;
  emitChange();
}

function stripTwistPrefix(twist: string | undefined): string {
  return (twist ?? '').replace(/^\s*\*\*twist\*\*:\s*/i, '');
}

function cloneSection(section: IBattleplanContentSection): IBattleplanContentSection {
  const cloned: IBattleplanContentSection = {
    title: section.title,
    text: section.text ?? '',
    abilities: [...(section.abilities ?? [])],
  };
  if (section.trailingText) cloned.trailingText = section.trailingText;
  return cloned;
}

function cleanSection(section: IBattleplanContentSection): IBattleplanContentSection {
  const cleaned: IBattleplanContentSection = {
    title: section.title.trim(),
  };
  const text = section.text?.trim();
  const trailingText = section.trailingText?.trim();
  if (text) cleaned.text = text;
  if (section.abilities?.length) cleaned.abilities = section.abilities;
  if (trailingText) cleaned.trailingText = trailingText;
  return cleaned;
}

function hasSectionContent(section: IBattleplanContentSection): boolean {
  return (
    !!section.title ||
    !!section.text ||
    !!section.trailingText ||
    (section.abilities?.length ?? 0) > 0
  );
}

function addSection() {
  localSections.value = [...localSections.value, { title: '', text: '', abilities: [] }];
  emitChange();
}

function addCommonSection(title: string) {
  localSections.value = [...localSections.value, { title, text: '', abilities: [] }];
  emitChange();
}

function removeSection(index: number) {
  localSections.value = localSections.value.filter((_, i) => i !== index);
  emitChange();
}

function updateSection(index: number, patch: Partial<IBattleplanContentSection>) {
  localSections.value = localSections.value.map((section, i) =>
    i === index ? { ...section, ...patch } : section,
  );
  emitChange();
}

function isTrailingTextOpen(index: number, section: IBattleplanContentSection): boolean {
  return trailingTextOpen.value.has(index) || !!section.trailingText?.trim();
}

function showTrailingText(index: number) {
  trailingTextOpen.value = new Set([...trailingTextOpen.value, index]);
}
</script>

<style scoped>
.battleplan-form {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}

.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}

.section-heading {
  margin-bottom: var(--st-space-1);
}

.section-heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--st-space-3);
}

.section-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--st-space-2);
}

.section-fields {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
}

.trailing-text-add {
  align-self: flex-start;
}
</style>
